import type { TeamRoleAttributes as GQLInput } from '../../../generated/api.graphql';
import { take } from '../../../util';

/**
 * Alias, not a mirror. `TeamRoleAttributes` is already the shape the role forms build, so a
 * domain restatement would map nothing and be free to drift from the schema.
 */
export type TeamRoleInput = GQLInput;

/**
 * Every `TeamRoleAttributes` field, enumerated from the schema. The whitelist earns its place for
 * one reason: the legacy `TeamRoleInput` carried a **required** `accountId` and both role pages
 * still add one, while AppSchema takes the account from the `X-VP-Account-Id` header and has no
 * such field — passing it through would fail the whole mutation on an unknown input field.
 *
 * `permissionAttributes` is deliberately not whitelisted key by key: it is a fixed, typed, 49-key
 * input whose key set is identical to the legacy `PermissionAttributesInput`, so an inner list
 * would guard against nothing and be 49 more lines free to drift on the next codegen.
 *
 * `take()` drops `undefined` but **not** `null`, so an explicit `id: null` would reach the wire.
 * Harmless only because `RoleNew` initialises `id: undefined` and `RoleEdit` guards on it.
 */
const GQL_FIELDS = ['id', 'name', 'permissionAttributes'] as const;

/**
 * Compile-time exhaustiveness guard. `take()` errors on a misspelled key but not on a *missing*
 * one, so without this a field added to the schema by a later codegen run would silently stop
 * persisting. This turns that into a build failure.
 */
type _AllGqlFieldsListed =
  [Exclude<keyof GQLInput, (typeof GQL_FIELDS)[number]>] extends [never] ? true : never;
const _allGqlFieldsListed: _AllGqlFieldsListed = true;
void _allGqlFieldsListed;

export const toGqlInput = (input: TeamRoleInput): GQLInput =>
  take<GQLInput, GQLInput>(input, ...GQL_FIELDS);
